import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  FieldValues,
  FieldPath,
  UseControllerProps,
  useFormState,
} from 'react-hook-form';
import FormField from '@ingka/form-field';
import Switch from '@ingka/switch';

import { getter } from '@/lib/utils/object';
import { extractErrorMessage } from '../error-message';

export type ControlledSwitchProps<T extends FieldValues> = {
  className?: string;
  // Form
  name: FieldPath<T>;
  rules?: UseControllerProps<T>['rules'];
  forcedError?: any;
  // Switch props
  label: string;
  readOnly?: boolean;
  shouldValidate?: boolean;
  successMessage?: string;
  onChange?: (checked: boolean) => void;
};

export function ControlledSwitch<T extends FieldValues>(
  props: PropsWithChildren<ControlledSwitchProps<T>>
) {
  const { t } = useTranslation();
  const {
    name,
    rules,
    shouldValidate = true,
    label,
    readOnly,
    successMessage,
    onChange,
    forcedError,
  } = props;
  // Error handling
  const { errors } = useFormState({ name });
  const error = forcedError || getter(errors, name);
  const errorMsgArgs = error && extractErrorMessage(error);
  const errorMsg = error && t(...errorMsgArgs);

  return (
    <div className={props.className}>
      <Controller
        name={name}
        rules={rules}
        render={({ field }) => (
          <FormField
            fieldHelper={{
              msg: shouldValidate ? successMessage : '',
              type: 'error',
            }}
            validation={{
              msg: errorMsg,
              type: 'error',
            }}
            valid={!error}
            shouldValidate={!!shouldValidate}
          >
            <Switch
              {...field}
              data-testid={`${name}Switch`}
              onChange={(e) => {
                const checked = e.target.checked;
                field.onChange(checked);
                onChange?.(checked);
              }}
              id={name}
              label={label}
              disabled={readOnly}
              checked={field.value || false}
            />
          </FormField>
        )}
      />
    </div>
  );
}

export default ControlledSwitch;
